import type { DataRow } from "../types";
import { normalizeDmaCode, type DmaInfluenceRecord, type SourceInfluenceSnapshot } from "./sourceInfluence";

export type AlertCategory = "น้ำไม่ไหล" | "น้ำไหลอ่อน";
export type AlertDayType = "weekday" | "saturday" | "sunday" | "unknown";
export type AlertMetricMode = "allocated" | "related";
export type AlertSort = "allocated" | "related" | "days" | "repeatDmas" | "recent";

export type ServiceAlertFilters = {
  dateFrom: string;
  dateTo: string;
  categories: AlertCategory[];
  station: string;
  branches: string[];
  dma: string;
  pressurePoint: string;
  dayTypes: AlertDayType[];
  minCount: number;
  minDistinctDays: number;
  repeatOnly: boolean;
};

export const defaultServiceAlertFilters: ServiceAlertFilters = {
  dateFrom: "",
  dateTo: "",
  categories: ["น้ำไม่ไหล", "น้ำไหลอ่อน"],
  station: "",
  branches: [],
  dma: "",
  pressurePoint: "",
  dayTypes: ["weekday", "saturday", "sunday"],
  minCount: 3,
  minDistinctDays: 2,
  repeatOnly: true,
};

export type ServiceAlertJob = {
  key: string;
  row: DataRow;
  category: AlertCategory;
  date: string;
  dateTime: string;
  hour: number | null;
  dayType: AlertDayType;
  dmaCode: string;
  branchNumber: string;
  pressurePoint: string;
  record: DmaInfluenceRecord | null;
  allocations: { station: string; factor: number }[];
};

export type StationAlertMetric = {
  station: string;
  relatedJobs: number;
  allocatedJobs: number;
  distinctDays: number;
  dmaCount: number;
  repeatDmas: number;
  pressurePointCount: number;
  latestAt: string;
  peakHour: number | null;
  byDayTypeHour: Record<AlertDayType, { related: number; allocated: number }[]>;
};

export type DmaAlertMetric = {
  dmaCode: string;
  branchNumber: string;
  pressurePoint: string;
  relatedJobs: number;
  allocatedJobs: number;
  distinctDays: number;
  latestAt: string;
  stations: { station: string; factor: number }[];
  qualifies: boolean;
};

export type ServiceAlertAnalysis = {
  jobs: ServiceAlertJob[];
  rankings: StationAlertMetric[];
  dmaMetrics: DmaAlertMetric[];
  repeatedDmas: DmaAlertMetric[];
  mappedJobs: number;
  unmappedJobs: number;
  unknownPressureJobs: number;
  weightedTotal: number;
  distinctDays: number;
  byDayTypeHour: Record<AlertDayType, { related: number; allocated: number }[]>;
};

const UNKNOWN_STATION = "ไม่ระบุสถานีสูบจ่าย";
export const UNKNOWN_PRESSURE_POINT = "ไม่ระบุจุดวัดแรงดัน";

function text(value: unknown): string {
  return String(value ?? "").trim();
}

export function alertCategory(value: unknown): AlertCategory | null {
  const compact = text(value).toLocaleLowerCase("th").replace(/\s+/g, "");
  if (/น้ำไม่ไหล/.test(compact)) return "น้ำไม่ไหล";
  if (/น้ำ(?:ไหล)?อ่อน|แรงดันน้ำต่ำ/.test(compact)) return "น้ำไหลอ่อน";
  return null;
}

export function alertDateTime(row: DataRow): string {
  const received = text(row.receivedAt);
  if (received) return received;
  const opened = text(row.openedAt);
  if (opened.includes("T")) return opened;
  const time = text(row.receivedTime).match(/\b(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (opened && time) return `${opened.slice(0, 10)}T${time[1].padStart(2, "0")}:${time[2]}:${time[3] ?? "00"}+07:00`;
  return opened;
}

export function alertHour(row: DataRow): number | null {
  const match = alertDateTime(row).match(/(?:T|\s)(\d{1,2}):\d{2}/);
  const hour = match ? Number(match[1]) : Number.NaN;
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : null;
}

export function alertDayType(date: string): AlertDayType {
  if (!/^\d{4}-\d{2}-\d{2}/.test(date)) return "unknown";
  const parsed = new Date(`${date.slice(0, 10)}T12:00:00+07:00`);
  if (Number.isNaN(parsed.getTime())) return "unknown";
  const day = parsed.getDay();
  return day === 0 ? "sunday" : day === 6 ? "saturday" : "weekday";
}

function jobKey(row: DataRow): string {
  return text(row.jobId) || row.__rowId;
}

function emptyHours(): { related: number; allocated: number }[] {
  return Array.from({ length: 24 }, () => ({ related: 0, allocated: 0 }));
}

function emptyDayHours(): Record<AlertDayType, { related: number; allocated: number }[]> {
  return { weekday: emptyHours(), saturday: emptyHours(), sunday: emptyHours(), unknown: emptyHours() };
}

function allocations(record: DmaInfluenceRecord | null): { station: string; factor: number }[] {
  if (!record?.sources.length) return [{ station: UNKNOWN_STATION, factor: 1 }];
  const positive = record.sources.filter((source) => source.normalizedFactor > 0);
  if (!positive.length) {
    const factor = 1 / record.sources.length;
    return record.sources.map((source) => ({ station: source.name, factor }));
  }
  const total = positive.reduce((sum, source) => sum + source.normalizedFactor, 0);
  return positive.map((source) => ({ station: source.name, factor: source.normalizedFactor / total }));
}

function uniqueAlertJobs(snapshot: SourceInfluenceSnapshot, rows: DataRow[]): ServiceAlertJob[] {
  const recordByDma = new Map(snapshot.records.map((record) => [normalizeDmaCode(record.dmaCode), record]));
  const seen = new Set<string>();
  const result: ServiceAlertJob[] = [];
  for (const row of rows) {
    const category = alertCategory(row.workType);
    if (!category) continue;
    const key = jobKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    const dmaCode = normalizeDmaCode(row.dmaCode);
    const record = recordByDma.get(dmaCode) ?? null;
    const dateTime = alertDateTime(row);
    const date = dateTime.slice(0, 10);
    result.push({
      key,
      row,
      category,
      date,
      dateTime,
      hour: alertHour(row),
      dayType: alertDayType(date),
      dmaCode,
      branchNumber: record?.branchNumber ?? text(row.branchNumber),
      pressurePoint: record?.pressurePoint ?? UNKNOWN_PRESSURE_POINT,
      record,
      allocations: allocations(record),
    });
  }
  return result;
}

function applyFilters(jobs: ServiceAlertJob[], filters: ServiceAlertFilters): ServiceAlertJob[] {
  return jobs.filter((job) => {
    if (filters.categories.length && !filters.categories.includes(job.category)) return false;
    if (filters.dateFrom && (!job.date || job.date < filters.dateFrom)) return false;
    if (filters.dateTo && (!job.date || job.date > filters.dateTo)) return false;
    if (filters.station && !job.allocations.some((item) => item.station === filters.station)) return false;
    if (filters.branches.length && !filters.branches.includes(job.branchNumber)) return false;
    if (filters.dma && job.dmaCode !== normalizeDmaCode(filters.dma)) return false;
    if (filters.pressurePoint && job.pressurePoint !== filters.pressurePoint) return false;
    if (filters.dayTypes.length && !filters.dayTypes.includes(job.dayType)) return false;
    return true;
  });
}

function buildDmaMetrics(jobs: ServiceAlertJob[], filters: ServiceAlertFilters): DmaAlertMetric[] {
  const grouped = new Map<string, ServiceAlertJob[]>();
  for (const job of jobs) {
    const key = job.dmaCode || "NO-DMA";
    grouped.set(key, [...(grouped.get(key) ?? []), job]);
  }
  return [...grouped.entries()].map(([key, items]) => {
    const first = items[0];
    const dates = new Set(items.map((item) => item.date).filter(Boolean));
    const stationFactor = new Map<string, number>();
    for (const item of first.allocations) stationFactor.set(item.station, item.factor);
    const allocated = filters.station
      ? items.reduce((sum, item) => sum + (item.allocations.find((source) => source.station === filters.station)?.factor ?? 0), 0)
      : items.length;
    return {
      dmaCode: key === "NO-DMA" ? "" : key,
      branchNumber: first.branchNumber,
      pressurePoint: first.pressurePoint,
      relatedJobs: items.length,
      allocatedJobs: allocated,
      distinctDays: dates.size,
      latestAt: items.map((item) => item.dateTime).sort().at(-1) ?? "",
      stations: [...stationFactor].map(([station, factor]) => ({ station, factor })),
      qualifies: items.length >= filters.minCount && dates.size >= filters.minDistinctDays,
    };
  }).sort((a, b) => b.relatedJobs - a.relatedJobs || b.distinctDays - a.distinctDays || b.latestAt.localeCompare(a.latestAt));
}

export function buildServiceAlertAnalysis(snapshot: SourceInfluenceSnapshot, rows: DataRow[], filters: ServiceAlertFilters): ServiceAlertAnalysis {
  const jobs = applyFilters(uniqueAlertJobs(snapshot, rows), filters);
  const dmaMetrics = buildDmaMetrics(jobs, filters);
  const repeatedDmas = dmaMetrics.filter((item) => item.qualifies);
  const repeatedSet = new Set(repeatedDmas.map((item) => item.dmaCode || "NO-DMA"));
  const scopedJobs = filters.repeatOnly ? jobs.filter((job) => repeatedSet.has(job.dmaCode || "NO-DMA")) : jobs;
  const rankingMap = new Map<string, {
    jobs: Set<string>; dates: Set<string>; dmas: Set<string>; repeatDmas: Set<string>; pressure: Set<string>;
    allocated: number; latest: string; hours: Record<AlertDayType, { related: number; allocated: number }[]>;
  }>();
  const dayHours = emptyDayHours();
  let weightedTotal = 0;
  for (const job of scopedJobs) {
    const activeAllocations = filters.station ? job.allocations.filter((item) => item.station === filters.station) : job.allocations;
    for (const allocation of activeAllocations) {
      const current = rankingMap.get(allocation.station) ?? {
        jobs: new Set(), dates: new Set(), dmas: new Set(), repeatDmas: new Set(), pressure: new Set(),
        allocated: 0, latest: "", hours: emptyDayHours(),
      };
      current.jobs.add(job.key);
      if (job.date) current.dates.add(job.date);
      current.dmas.add(job.dmaCode || "NO-DMA");
      if (repeatedSet.has(job.dmaCode || "NO-DMA")) current.repeatDmas.add(job.dmaCode || "NO-DMA");
      current.pressure.add(job.pressurePoint);
      current.allocated += allocation.factor;
      current.latest = current.latest > job.dateTime ? current.latest : job.dateTime;
      if (job.hour !== null) {
        current.hours[job.dayType][job.hour].related += 1;
        current.hours[job.dayType][job.hour].allocated += allocation.factor;
        dayHours[job.dayType][job.hour].related += 1;
        dayHours[job.dayType][job.hour].allocated += allocation.factor;
      }
      rankingMap.set(allocation.station, current);
      weightedTotal += allocation.factor;
    }
  }
  const rankings: StationAlertMetric[] = [...rankingMap].map(([station, current]) => {
    const totals = Array.from({ length: 24 }, (_, hour) => (['weekday', 'saturday', 'sunday', 'unknown'] as AlertDayType[])
      .reduce((sum, kind) => sum + current.hours[kind][hour].allocated, 0));
    const max = Math.max(0, ...totals);
    return {
      station,
      relatedJobs: current.jobs.size,
      allocatedJobs: current.allocated,
      distinctDays: current.dates.size,
      dmaCount: current.dmas.size,
      repeatDmas: current.repeatDmas.size,
      pressurePointCount: current.pressure.size,
      latestAt: current.latest,
      peakHour: max > 0 ? totals.indexOf(max) : null,
      byDayTypeHour: current.hours,
    };
  }).sort((a, b) => b.allocatedJobs - a.allocatedJobs || b.distinctDays - a.distinctDays || b.repeatDmas - a.repeatDmas || a.station.localeCompare(b.station, "th"));

  return {
    jobs: scopedJobs,
    rankings,
    dmaMetrics,
    repeatedDmas,
    mappedJobs: scopedJobs.filter((job) => job.record?.sources.length).length,
    unmappedJobs: scopedJobs.filter((job) => !job.record?.sources.length).length,
    unknownPressureJobs: scopedJobs.filter((job) => job.pressurePoint === UNKNOWN_PRESSURE_POINT).length,
    weightedTotal,
    distinctDays: new Set(scopedJobs.map((job) => job.date).filter(Boolean)).size,
    byDayTypeHour: dayHours,
  };
}

export function sortStationMetrics(metrics: StationAlertMetric[], sort: AlertSort): StationAlertMetric[] {
  return [...metrics].sort((a, b) => {
    if (sort === "related") return b.relatedJobs - a.relatedJobs || b.allocatedJobs - a.allocatedJobs;
    if (sort === "days") return b.distinctDays - a.distinctDays || b.allocatedJobs - a.allocatedJobs;
    if (sort === "repeatDmas") return b.repeatDmas - a.repeatDmas || b.allocatedJobs - a.allocatedJobs;
    if (sort === "recent") return b.latestAt.localeCompare(a.latestAt) || b.allocatedJobs - a.allocatedJobs;
    return b.allocatedJobs - a.allocatedJobs || b.distinctDays - a.distinctDays || b.repeatDmas - a.repeatDmas;
  });
}
